'use client';

import { useState, useCallback, useTransition, useEffect } from 'react';
import { isLoggedInUser } from '@pixum/auth-core-ui';
import Button, { ButtonVariants, ButtonTypes, ButtonSizes } from '@pixum/button';
import { TextInput } from '@pixum/web-input';
import Label from '@pixum/label';
import { ensureSession } from 'services/session/client';
import { AuthDialog } from '../../../auth';
import { getOrderIdFromWindowLocation } from '../../utils/getOrderIdFromWindowLocation';
import styles from './OrderStatusForm.module.css';
import type { OrderStatusFormProps } from './types';

/**
 * OrderStatusForm component - form for checking order status
 * Client Component - handles form submission and Server Action integration
 *
 * Features:
 * - URL pre-fill capability (extracts order ID from query/hash parameters)
 * - Pixum design system Button and TextInput components
 * - Help link displayed as inline label (not a separate button)
 * - Authentication check before submission
 * - AuthDialog integration for login
 * - Post-login retry logic
 * - React 19 useTransition for pending states
 * - Built-in loading spinner via Button isBusy prop
 * - Error display via TextInput hasError prop
 */
export function OrderStatusForm({
  buttonText,
  helpLinkText,
  onHelpClick,
  label,
  onSubmitOrderStatus,
  onOrderStatusRetrieved,
}: OrderStatusFormProps) {
  // Extract order ID from URL on mount (client-side only)
  const [initialOrderId, setInitialOrderId] = useState('');

  useEffect(() => {
    // Client-side only: check URL for order ID parameter
    const urlOrderId = getOrderIdFromWindowLocation();

    if (urlOrderId) {
      setInitialOrderId(urlOrderId);
    }
  }, []);

  const [orderId, setOrderId] = useState(initialOrderId);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  // Update orderId when initialOrderId is set from URL
  useEffect(() => {
    if (initialOrderId && !orderId) {
      setOrderId(initialOrderId);
    }
  }, [initialOrderId, orderId]);

  const submitOrderStatus = useCallback(
    async (orderIdToSubmit: string) => {
      setError(null);

      try {
        const result = await onSubmitOrderStatus(orderIdToSubmit);

        if (result.success) {
          if (onOrderStatusRetrieved && result.orderData) {
            onOrderStatusRetrieved({
              orderData: result.orderData,
              ...(result.detailedOrderData ? { detailedOrderData: result.detailedOrderData } : undefined),
            });
          }
        } else {
          // Display error from server
          setError(result.error?.message || 'An error occurred. Please try again.');

          // If session expired, show auth dialog
          if (result.error?.code === 'SESSION_EXPIRED' || result.error?.code === 'UNAUTHORIZED') {
            setPendingOrderId(orderIdToSubmit);
            setShowAuthDialog(true);
          }
        }
      } catch (err) {
        setError('An unexpected error occurred. Please try again.');
        console.error('Order status check error:', err);
      }
    },
    [onSubmitOrderStatus, onOrderStatusRetrieved],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);

    // The whole flow runs in the transition so the button is busy while waiting for the session.
    startTransition(async () => {
      // A fast submit waits for the client session: without a JWT cookie yet, isLoggedInUser() would be false.
      try {
        await ensureSession();
      } catch {
        setError('An unexpected error occurred. Please try again.');

        return;
      }

      // Check authentication before submitting
      if (!isLoggedInUser()) {
        // Store the order ID to retry after login
        setPendingOrderId(orderId);
        setShowAuthDialog(true);

        return;
      }

      await submitOrderStatus(orderId);
    });
  };

  const handleAuthSuccess = useCallback(async () => {
    // After successful authentication, retry the submission if there was a pending order ID
    if (pendingOrderId) {
      startTransition(async () => {
        await submitOrderStatus(pendingOrderId);
        setPendingOrderId(null);
      });
    }
  }, [pendingOrderId, submitOrderStatus]);

  const handleAuthClose = useCallback(() => {
    setShowAuthDialog(false);
    setPendingOrderId(null);
  }, []);

  // Handle help link click
  const handleHelpLinkClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (onHelpClick) {
        onHelpClick();
      }
    },
    [onHelpClick],
  );

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
        <TextInput
          label={label}
          value={orderId}
          onChange={e => setOrderId(e.target.value)}
          required
          hasError={!!error}
          additionalInputInfo={error || undefined}
          inputInfo={
            helpLinkText && onHelpClick ? (
              <Label className={styles.inputInfoLabel} onClick={handleHelpLinkClick} text={helpLinkText} />
            ) : undefined
          }
          className={styles.formInput}
          name="orderId"
          dataTestId="order-status-input"
          autoComplete="off"
        />

        <Button
          className={styles.orderStatusButton}
          type={ButtonTypes.SUBMIT}
          variant={ButtonVariants.PRIMARY}
          size={ButtonSizes.MEDIUM}
          isBusy={isPending}
          label={buttonText}
        >
          {buttonText}
        </Button>
      </form>

      <AuthDialog
        isOpen={showAuthDialog}
        onClose={handleAuthClose}
        onSuccess={handleAuthSuccess}
        showSidePanel={true}
      />
    </>
  );
}
